import React, {useState, useEffect, useCallback, useRef} from "react";
import useCountriesData from "../../hooks/useContriesData";
import useData from "../../hooks/useData";
import { MAX_BOUNDS, MIN_ZOOM, MAX_ZOOM, DEFAULT_MAP_STATE} from "../../config/config";
import { slugify } from "../../hooks/utils";
import { WebMercatorViewport } from "react-map-gl";
import useComponentSize from "@rehooks/component-size";
import DeckGL, { GeoJsonLayer} from "deck.gl";
import MapControls from "../common/map/MapControls";
import MapToolTip from "../common/mapTooltip/MapToolTip";
import styles from "./styles.module.scss";
import PopUpDetail from "../common/popup/PopUpDetail";
import ListItem from "../common/list/ListItem";
import Accordion from "../common/accordion/Accordion";

const MapCities = () => {
  const [radioboxSelected, setRadioboxSelected] = useState('top20');
  const [hoveredCity, setHoveredCity] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [allCitiesData, setAllCitiesData] = useState(null);
  const [dataGroup, setDataGroup] = useState([]);
  const [showId, setShowId] = useState();
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [unselectedCountries, setUnselectedCountries] = useState([]);
  const [minZoom, setMinZoom] = useState(MIN_ZOOM);
  const [detailCard, setDetailCard] = useState('');
  const [showCard, setShowCard] = useState(false);
  const [viewport, setViewport] = useState(DEFAULT_MAP_STATE);
  let mapRef = useRef(null);
  let listRef = useRef(null);
  let { width, height } = useComponentSize(mapRef);
  const data = useData();
  const countriesData = useCountriesData();
  const popUpRef = useRef(null)

  useEffect(() => {
    if (width && height) {
      const newViewport = new WebMercatorViewport({
        width: width,
        height: height,
        maxZoom: MAX_ZOOM,
        minZoom: MIN_ZOOM,
        zoom: 0,
        longitude: 0,
        latitude: 0,
      }).fitBounds(MAX_BOUNDS);

      setMinZoom(newViewport.zoom);
      setViewport(newViewport);
    }
  }, [width, height]);

  const handleViewStateChange = useCallback((newState) => {
    if (newState.oldViewState !== null) {
      // clamp the zoom
      newState.viewState.zoom =
        newState.viewState.zoom > MAX_ZOOM ? MAX_ZOOM : newState.viewState.zoom;
      newState.viewState.zoom =
        newState.viewState.zoom < MIN_ZOOM ? MIN_ZOOM : newState.viewState.zoom;
      setViewport((v) =>
        v
          ? new WebMercatorViewport({
              ...v,
              ...newState.viewState,
            })
          : null
      );
    }
  }, []);

  const resetZoom = useCallback(() => {
    const newViewport = new WebMercatorViewport({
      width: width,
      height: height,
      maxZoom: MAX_ZOOM,
      minZoom: MIN_ZOOM,
      zoom: 0,
      longitude: 0,
      latitude: 0,
    }).fitBounds(MAX_BOUNDS);

    setViewport(newViewport);
  }, [width, height]);

  const zoomIncrement = useCallback(
    (amt) => {
      const newZoom = Math.max(
        Math.min(Math.max(viewport.zoom + amt, 0.001), MAX_ZOOM),
        minZoom
      );
      handleViewStateChange({
        viewState: {
          zoom: newZoom,
          transitionDuration: 5000,
        },
      });
    },
    [viewport, handleViewStateChange, minZoom]
  );


  useEffect(() => {

    const showVisibleCountries = (includeCountries, features) =>{
      const filteredFeatures = features.filter((feature) =>
        includeCountries.includes(feature.properties.ISO3)
      );
      const visibleCountries = {
        type: "FeatureCollection",
        features: filteredFeatures,
      };
      setSelectedCountries(visibleCountries);
    }

    const hideNotVisibleCountries = (includeCountries, features) =>{
        const unfilteredFeatures = features.filter(
          (feature) => !includeCountries.includes(feature.properties.ISO3)
        );
        const notVisibleCountries = {
          type: "FeatureCollection",
          features: unfilteredFeatures,
        };
        setUnselectedCountries(notVisibleCountries);
    }

    const groupBy = prop => data => {
        return data.reduce((dict, item) => {
          const { [prop]: _, ...rest } = item;
          dict[item[prop]] = [...(dict[item[prop]] || []), rest];
          return dict;
        }, {});
    };

    if(data && countriesData){

        const filteredTop20 = data.filter((item) =>
            item.top20 === true
        ).sort((a, b)=> 
            b.millionaires - a.millionaires
        );

        const filteredFastestFrowth = data.filter((item) =>
            item.HNWIGrowth >= 5
        ).sort((a, b)=> 
            b.HNWIGrowth - a.HNWIGrowth
        );

        const filteredTop5ByRegion = data.filter((item) =>
            item.top5ByRegion === true
        ).sort((a, b)=>  
            b.millionaires - a.millionaires
        );

        const dataByRegion = Object.entries(groupBy('region')(filteredTop5ByRegion))
                                    .map(([key, value]) => ({ name: key, cities: value }));
        const sortedDataByRegion = dataByRegion.sort((a, b)=> {return a.name.localeCompare(b.name);});

        const includeTop20Countries = filteredTop20.map((d) => d.ISO3);
        const includeTop5ByRegion = filteredTop5ByRegion.map((d) => d.ISO3);
        const includeFastestFrowth = filteredFastestFrowth.map((d) => d.ISO3);

        let features = countriesData.features; 

        setDataGroup(sortedDataByRegion);

        if(radioboxSelected === 'top20'){
            setAllCitiesData(filteredTop20);
            showVisibleCountries(includeTop20Countries, features);
            hideNotVisibleCountries(includeTop20Countries, features);
           
        }else if(radioboxSelected === 'fastestGrowth'){
            setAllCitiesData(filteredFastestFrowth);
            showVisibleCountries(includeFastestFrowth, features);
            hideNotVisibleCountries(includeFastestFrowth, features);
        }else if(radioboxSelected === 'top5ByRegion'){
            setAllCitiesData(filteredTop5ByRegion);
            showVisibleCountries(includeTop5ByRegion, features);
            hideNotVisibleCountries(includeTop5ByRegion, features);
        }
        
    }
  }, [countriesData, data, radioboxSelected]);

  const getProjectionCoordinates = (coords) => {
    if (viewport) {
      return viewport.project([coords[0], coords[1]]);
    }
    return [];
  };

  const handleRadioboxChange = (event) =>{
    setRadioboxSelected(event.target.value);
    resetZoom();
    setShowCard(false);
    setSelectedCity(null);
  }

  const handleHover = (data, fromMap = false) => {
    setHoveredCity(data !== null ? data.city : null);
    if (data && fromMap && radioboxSelected !== 'top5ByRegion') {
      const element = document.getElementById(`list-item-${slugify(data.city)}`);
      listRef.current.scrollTo(0, element.offsetTop - 30);
    }
  };

  const handleClick = (data) =>{
    setDetailCard(data);
    setSelectedCity(data.city);
    setShowCard(true);
    popUpRef.current.scrollIntoView({ behavior: "smooth" });
    handleViewStateChange({
      viewState: {
        zoom: 2,
        longitude: data.longitude,
        latitude: data.latitude,
        transitionDuration: 5000,
      },
    });
  }

  const handleShow = (id) => {
    setShowId(showId === id ? -1 : id);
  }

  const cardClose = () =>{
    setShowCard(false);
  }

  const intro = () =>{
    if(radioboxSelected === 'top20'){
        return (
            <React.Fragment>
                <h3>Top 20 Cities with the Most Millionaires in 2022</h3>
                <p>The high-net-worth-individual (HNWI) population data represents the top 20 cities globally in terms of resident millionaires (namely, individuals with wealth of USD 1 million or more) as of 30 June 2022. Of the 20 cities listed, 14 are in countries that host formal <a className="text-underline" target="_blank" href="https://www.henleyglobal.com/countries" rel="noreferrer">investment migration</a> programs and actively encourage foreign direct investment in return for <a className="text-underline" target="_blank" href="https://www.henleyglobal.com/residence-investment" rel="noreferrer">residence</a> or <a className="text-underline" target="_blank" href="https://www.henleyglobal.com/citizenship-investment" rel="noreferrer">citizenship</a> rights.</p>
                <p>Click on the map or the list below to see each city’s HNWI population and other data.</p>
            </React.Fragment>
        );
    }else if(radioboxSelected === 'top5ByRegion'){
        return (
            <React.Fragment>
                <h3>Top 5 Cities with the Most Millionaires by Region in 2022</h3>
                <p>The high-net-worth-individual (HNWI) population data represents the top 5 cities in five key regions in terms of the number of resident millionaires (namely, individuals with wealth of USD 1 million or more) as of 30 June 2022. Of the 25 cities, 12 are in countries that host formal <a className="text-underline" target="_blank" href="https://www.henleyglobal.com/countries" rel="noreferrer">investment migration</a> programs and actively encourage foreign direct investment in return for <a className="text-underline" target="_blank" href="https://www.henleyglobal.com/residence-investment" rel="noreferrer">residence</a> or <a className="text-underline" target="_blank" href="https://www.henleyglobal.com/citizenship-investment" rel="noreferrer">citizenship</a> rights.</p>
                <p>Click on the map or the list below to see each city’s HNWI population and other data.</p>
            </React.Fragment>
        );
    }else if(radioboxSelected === 'fastestGrowth'){
        return (
            <React.Fragment>
                <h3>Cities with Fast-Growing Millionaire Populations in 2022</h3>
                <p>The high-net-worth-individual (HNWI) population data represents 25 of the fastest growing cities in the world in terms of the number of resident millionaires (namely, individuals with wealth of USD 1 million or more) between 1 January and 30 June 2022. Of the 25 cities listed, 12 are in countries that host formal <a className="text-underline" target="_blank" href="https://www.henleyglobal.com/countries" rel="noreferrer">investment migration</a> programs and actively encourage foreign direct investment in return for <a className="text-underline" target="_blank" href="https://www.henleyglobal.com/residence-investment" rel="noreferrer">residence</a> or <a className="text-underline" target="_blank" href="https://www.henleyglobal.com/citizenship-investment" rel="noreferrer">citizenship</a> rights.</p>
                <p>Click on the map or the list below to see each city’s HNWI population and other data.</p>
            </React.Fragment>
        );
    }
  }


  return (

    <div className="innerContainer">
        <div className="custom-radiobox-group mt-4 mb-4" style={{'width': '100%', 'maxWidth': '450px'}}>
            <label className="custom-radiobox">
              <input type="radio" value="top20" name="radio" checked={radioboxSelected === 'top20'} onChange={handleRadioboxChange} /> 
              <span className="label-text">TOP 20</span>
            </label>
            <label className="custom-radiobox">
                <input type="radio" value="top5ByRegion" name="radio" checked={radioboxSelected === 'top5ByRegion'} onChange={handleRadioboxChange} /> 
                <span className="label-text">TOP 5 BY REGION</span>
            </label>
            <label className="custom-radiobox">
              <input type="radio" value="fastestGrowth" name="radio" checked={radioboxSelected === 'fastestGrowth'} onChange={handleRadioboxChange} />
              <span className="label-text">FASTEST GROWTH</span>
            </label>
        </div>
        <div className="row flex-md-row-reverse">
          <div className="col-12">
            {intro()}
          </div>
        </div>

        <div className="row flex-md-row-reverse">
          <div className="col-12 col-md col-lg">
            <div className={styles.mapColumn}>
            
                <div className={styles.mapWrapper} ref={mapRef}>
                  <DeckGL
                    viewState={viewport}
                    controller={{scrollZoom:false, doubleClickZoom: false}}
                    onViewStateChange={handleViewStateChange}
                  >
                    <GeoJsonLayer
                      id="map-unselected-countries"
                      data={unselectedCountries ? unselectedCountries : null}
                      getLineColor={[255, 255, 255]}
                      getFillColor={[233, 233, 233]}
                      lineWidthUnits={"pixels"}
                      stroked={true}
                    />
                    <GeoJsonLayer
                      id="map-selected-countries"
                      data={selectedCountries ? selectedCountries : null}
                      getLineColor={[255, 255, 255]}
                      getFillColor={[193, 197, 200]}
                      lineWidthUnits={"pixels"}
                      stroked={true}
                      pickable={true}
                    />
                  </DeckGL>
                  <svg
                    className={`${styles.cityMarkers}`}
                    height={"100%"}
                    width={"100%"}
                    viewBox={`0 0 ${width} ${height}`}
                  >
                    {allCitiesData &&
                      allCitiesData.map((d) => {
                        return (
                          <circle
                            className={`${styles.circle}`}
                            key={d.city + "-marker"}
                            cx={getProjectionCoordinates([d.longitude, d.latitude])[0]}
                            cy={getProjectionCoordinates([d.longitude, d.latitude])[1]}
                            r={hoveredCity === d.city ? 10 : 5}
                            fill={hoveredCity === d.city ? "#7DB0DA" : "#405363"}
                            stroke={"#fff"}
                            onTouchStart={() => {
                              handleHover(d, true);
                            }}
                            onMouseEnter={() => {
                              handleHover(d, true);
                            }}
                            onMouseOut={() => {
                              handleHover(null);
                            }}
                            onClick={() => handleClick(d)}
                          >
                            <title>{d.city}</title>
                          </circle>
                        );
                      })}
                  </svg>
                  {allCitiesData &&
                    allCitiesData.map((d) => {
                      return (
                        <React.Fragment key={d.city + "-tooltip"}>
                          {(selectedCity === d.city || hoveredCity === d.city) && (
                            <MapToolTip 
                              name={d.city} 
                              top={getProjectionCoordinates([d.longitude,d.latitude,])[1] - 10}
                              left={getProjectionCoordinates([d.longitude,d.latitude,])[0]}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                    <MapControls
                      zoomIncrement={() => zoomIncrement(0.5)}
                      zoomDecrement={() => zoomIncrement(-0.5)}
                      resetZoom={() => resetZoom()}
                    /> 
                </div>
            
                <div ref={popUpRef} className={styles.positionToScroll}></div>
                {showCard ? <PopUpDetail item={detailCard} onClose={cardClose} /> : null}
            </div>
            
          </div>
          <div className={`col-12 col-md-4 col-lg-3 ${styles.listColumnMinWidth}`}>
            {radioboxSelected === 'top20' || radioboxSelected === 'fastestGrowth' ? (
                <div className={styles.listWrapper}>
                    <div className={`d-flex align-items-center justify-content-between ${styles.listHeader}`}>
                      {radioboxSelected === 'top20' && (<span className={styles.headerRank}>Rank</span>)}
                        <span className={styles.headerCity}>City</span>
                        <span>Country</span>
                    </div>
                    <div className={styles.listContainer} ref={listRef}>
                            {allCitiesData &&
                            allCitiesData.map((d, index) => {
                                return (
                                <ListItem
                                    id={`list-item-${slugify(d.city)}`}
                                    key={d.city}
                                    style={{
                                    backgroundColor:
                                        hoveredCity === d.city ? "#C1C5C8" : null,
                                    }}
                                    onMouseOver={() => {
                                    handleHover(d,false);
                                    }}
                                    onMouseOut={() => {
                                    handleHover(null);
                                    }}
                                    onClick={() => handleClick(d)}
                                    data={d}
                                    rank={radioboxSelected === 'top20' ? index + 1 : ''}
                                />
                                );
                            })}                        
                    </div>
                </div>
                ):null
            }
            {radioboxSelected === 'top5ByRegion' ? (
                <div>
                    <div className={`d-flex align-items-center justify-content-between ${styles.listHeader}`}>
                        <span>Region</span>
                    </div>
                {dataGroup &&
                    dataGroup.map((d,index) => {
                    const listOfCities = d.cities;
                return(
                    <Accordion id={index} show={showId === index} onShow={()=>handleShow(index)} key={d.name}  title={d.name}>
                      
                        {listOfCities.map((item, index)=>{
                        return(
                            <ListItem
                                id={`list-item-${slugify(item.city)}`}
                                key={item.city}
                                style={{
                                backgroundColor:
                                    hoveredCity === item.city ? "#C1C5C8" : null,
                                }}
                                onMouseOver={() => {
                                handleHover(item,false);
                                }}
                                onMouseOut={() => {
                                handleHover(null);
                                }}
                                onClick={() => handleClick(item)}
                                data={item}
                                rank={index + 1}
                            />
                        )
                        })}

                    </Accordion>

                    );
                })}
                  <div className={`mt-3 ${styles.footerNote}`}>
                    <ul>
                      <li>San Francisco Bay Area includes the city of San Francisco and Silicon Valley.</li>
                      <li>Los Angeles & Malibu includes the city of Los Angeles, Beverly Hills, Laguna Beach, Los Angeles, Malibu, and Newport Beach.</li>
                      <li>Zurich includes the Canton of Zurich.</li>
                      <li>Geneva includes the Canton of Geneva.</li>
                      <li>Paris includes the city of Paris and Hauts-de-Seine.</li>
                    </ul>
                    <p>Figures to 30 June 2022. HNWI numbers are rounded to the nearest 100. Multi-millionaire figures are rounded to the nearest 10. All growth rates are effectively in US dollar terms as HNWIs are defined as individuals with net assets of USD 1 million or more. ‘Total wealth’ refers to the private wealth held by all the individuals living in each city. It includes all their investable assets (property, cash, equities) less any liabilities.</p>
                    <p>Source: New World Wealth</p>
                  </div>
                </div>
            ):null

            }
          </div>
        </div>
       {radioboxSelected !== 'top5ByRegion' ? (
        <div className="row">
          <div className={`col-12 col-md-4 col-lg-3 ${styles.listColumnMinWidth}`}>
              {radioboxSelected === 'top20' && 
              <div className={`mt-3 ${styles.footerNote}`}>
                <ul>
                  <li>San Francisco Bay Area includes the city of San Francisco and Silicon Valley.</li>
                  <li>Los Angeles & Malibu includes the city of Los Angeles, Beverly Hills, Laguna Beach, Los Angeles, Malibu, and Newport Beach.</li>
                  <li>Zurich includes the Canton of Zurich.</li>
                  <li>Geneva includes the Canton of Geneva.</li>
                  <li>Paris includes the city of Paris and Hauts-de-Seine.</li>
                </ul>

                <p>Figures to 30 June 2022. HNWI numbers are rounded to the nearest 100. Multi-millionaire figures are rounded to the nearest 10. All growth rates are effectively in US dollar terms as HNWIs are defined as individuals with net assets of USD 1 million or more. ‘Total wealth’ refers to the private wealth held by all the individuals living in each city. It includes all their investable assets (property, cash, equities) less any liabilities.</p>
                <p>Source: New World Wealth</p>
              </div>
              }
              {radioboxSelected === 'fastestGrowth' && 
              <div className={`mt-3 ${styles.footerNote}`}>
                <ul>
                  <li>Geneva includes the Canton of Geneva.</li>
                  <li>Miami includes Miami (city) and Miami Beach (island).</li>
                </ul>

                <p>Figures to 30 June 2022. HNWI numbers are rounded to the nearest 100. Multi-millionaire figures are rounded to the nearest 10. All growth rates are effectively in US dollar terms as HNWIs are defined as individuals with net assets of USD 1 million or more. ‘Total wealth’ refers to the private wealth held by all the individuals living in each city. It includes all their investable assets (property, cash, equities) less any liabilities.</p>

                <p>Disclaimer: Our research focuses on just over 150 cities globally, including all the major wealth hubs. However, as New World Wealth does not cover all the cities of the world, several mid-level wealth hubs may be missing from this high-growth list.</p>

                <p>Source: New World Wealth</p>
              </div>
              }
          </div>
        </div>
       ):null
       }
    </div>

  );
}

export default MapCities;